import type { User } from '@/@types/user'
import {
	BLOCK_USER,
	CLEAR_USER_INTERACTIONS,
	FOLLOWER_LIST,
	FOLLOWING_LIST,
	FOLLOW_USER,
	UNBLOCK_USER,
	UNFOLLOW_USER,
	USER_PROFILE,
} from '@/constants/api'
import { get, post } from '@/utils/request'
import type {
	InfiniteData,
	MutationOptions,
	UseInfiniteQueryOptions,
	UseMutationOptions,
	UseQueryOptions,
	UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import queryClient from '../queryClient'

export function getUserQueryOptions(username: string | undefined): UseQueryOptions<User> {
	return {
		queryKey: [USER_PROFILE, username],
		queryFn: async () => getUserProfile(username),
		staleTime: 1000 * 60 * 60 * 6,
	}
}

export const currentUserQueryOptions = getUserQueryOptions(void 0) as UseSuspenseQueryOptions<User>

export async function getUserProfile(username?: string) {
	return get(USER_PROFILE, {
		params: {
			username,
		},
	}).then((res) => res.data.user as User)
}

export async function followUser(username: string) {
	return post(FOLLOW_USER, {
		username,
	})
}

export async function unfollowUser(username: string) {
	return post(UNFOLLOW_USER, {
		username,
	})
}

export async function blockUser(username: string) {
	return post(BLOCK_USER, { username })
}

export async function unblockUser(username: string) {
	return post(UNBLOCK_USER, { username })
}

interface QueryUserListPage {
	data: User[]
	loadMoreKey?: unknown
}
export function getFollowingListQueryOptions(
	username: string
): UseInfiniteQueryOptions<QueryUserListPage, unknown, InfiniteData<QueryUserListPage>> {
	return {
		queryKey: [FOLLOWING_LIST, username],
		queryFn: ({ pageParam }) =>
			post<QueryUserListPage>(FOLLOWING_LIST, {
				limit: 20,
				loadMoreKey: pageParam,
				username,
			}).then((res) => {
				const body = res.data

				for (const user of body.data) {
					queryClient.setQueryData(getUserQueryOptions(user.username).queryKey, user)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryUserListPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getFollowerListQueryOptions(
	username: string
): UseInfiniteQueryOptions<QueryUserListPage, unknown, InfiniteData<QueryUserListPage>> {
	return {
		queryKey: [FOLLOWER_LIST, username],
		queryFn: ({ pageParam }) =>
			post<QueryUserListPage>(FOLLOWER_LIST, {
				limit: 20,
				loadMoreKey: pageParam,
				username,
			}).then((res) => {
				const body = res.data

				// cache posts
				for (const user of body.data) {
					queryClient.setQueryData(getUserQueryOptions(user.username).queryKey, user)
				}

				return body
			}),
		getNextPageParam: (lastPage: QueryUserListPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export function getBlockToggleMutationOptions(): MutationOptions<
	boolean,
	Error,
	string,
	{ prevBlocking: boolean }
> {
	return {
		mutationFn: async (username: string) => {
			const user = queryClient.getQueryData<User>(getUserQueryOptions(username).queryKey)
			if (!user) throw new Error('用户信息不存在')
			if (user.blocking) {
				await blockUser(username)
				return false
			} else {
				await unblockUser(username)
				return true
			}
		},
		onMutate: async (username: string) => {
			const user = queryClient.getQueryData<User>(getUserQueryOptions(username).queryKey)
			if (!user)
				return {
					prevBlocking: false,
				}
			const prevBlocking = !!user.blocking
			queryClient.setQueryData(getUserQueryOptions(username).queryKey, {
				...user,
				blocking: !prevBlocking,
			})
			return { prevBlocking }
		},
		onError: (_err: unknown, username: string, context?: { prevBlocking: boolean }) => {
			const user = queryClient.getQueryData<User>(getUserQueryOptions(username).queryKey)
			if (!user || !context) return
			queryClient.setQueryData(getUserQueryOptions(username).queryKey, {
				...user,
				blocking: context.prevBlocking,
			})
		},
	}
}

export const CLEAR_USER_INTERACTIONS_MUTATION_OPTIONS: UseMutationOptions<boolean, Error, string> =
	{
		mutationKey: [CLEAR_USER_INTERACTIONS],
		mutationFn: async (username: string) =>
			post(CLEAR_USER_INTERACTIONS, { username }).then((res) => res.data.success),
	}
